import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { RequirementInstance } from '../../models/Requirement'
import { Headline, SubHeadline } from '../../shared/components/Headlines'
import { RadioButtonControl } from '../../shared/components/RadioButtonControl'
import { SwitchControl } from '../../shared/components/SwitchControl'
import { Warning, WarningHeadline, WarningText } from '../../shared/components/Warning'
import { Jaw, JawTranslation } from '../../shared/Constants'
import { breakpoint, styled } from '../../shared/styled-components'
import { Utils } from '../../shared/Utils'
import { CalculatorStoreInstance } from '../../stores/CalculatorStore'
import { JawToothSelection } from './views/jaw/requirements/JawToothSelection'

interface IProps {
  store?: CalculatorStoreInstance
  jaw: Jaw
}

@inject('store')
@observer
export class Requirements extends React.Component<IProps> {
  // -------------------------------------------------------------------------- HELPERS

  private _getRequirement (): RequirementInstance {
    const { jaw } = this.props
    return this.props.store!.offer.getJawRequirement(jaw)
  }

  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private _onNoTeethChange (value: boolean) {
    const requirement = this._getRequirement()
    requirement.setNoTeeth(value)
  }

  @autobind
  private _onFullReplacementTypeChange (fullReplacementType: 'removable'|'non_removable') {
    const requirement = this._getRequirement()
    requirement.setFullReplacementType(fullReplacementType)
  }

  // -------------------------------------------------------------------------- RENDERING

  render () {
    const requirement = this._getRequirement()
    const { jaw } = this.props

    if (!requirement) return <div />

    const fullReplacementType = requirement.allOn4 ? 'non_removable' : requirement.fullReplacementType

    return (<Container>
      <Top>
        <Left>
          <ImageContainer>
            {requirement.noTeeth
              ? <NoTeethImage src={Utils.getAssetPath(`/assets/calculator/${jaw}_jaw_${fullReplacementType}.png`)} />
              : <JawToothSelection jaw={jaw} />}
          </ImageContainer>
          <SwitchControl
            label='Es sind keine Zähne mehr vorhanden.'
            value={requirement.noTeeth}
            onChange={this._onNoTeethChange} />
        </Left>
        <Right>
          <div>
            <Headline>{JawTranslation[jaw]}</Headline>
            <SubHeadline>{requirement.noTeeth ? 'Bitte Art des Zahnersatzes wählen' : 'Fehlende Zähne bitte anklicken'}</SubHeadline>

            {!requirement.noTeeth && <MissingTeethList>
              {requirement.sortedMissingTeeth.map(toothIndex =>
                <MissingTooth key={toothIndex}>{toothIndex}</MissingTooth>
              )}
            </MissingTeethList>}

            {requirement.noTeeth && <Checkboxes>
                <RadioButton
                  label='herausnehmbarer Zahnersatz'
                  checked={fullReplacementType === 'removable'}
                  onChange={this._onFullReplacementTypeChange.bind(this, 'removable')}
                  tooltip='Implantatgetragener, herausnehmbarer Zahnersatz weist eine hohe Stabilität auf, wird jedoch vom Patienten zur Reinigung und Pflege entfernt.' />
                <RadioButton
                  label='festsitzender Zahnersatz'
                  checked={fullReplacementType === 'non_removable'}
                  onChange={this._onFullReplacementTypeChange.bind(this, 'non_removable')}
                  tooltip='Festsitzender Zahnersatz kommt einem natürlichen Gebiss am nächsten und ist in Pflege und Ästhetik je nach Material nahezu identisch. ' />
            </Checkboxes>}
          </div>
          {/*{requirement.maximumMissingTeethReached() && !requirement.noTeeth && <Warning>*/}
          {/*  <WarningHeadline>Hinweis</WarningHeadline>*/}
          {/*  <WarningText>Sie haben die maximale Anzahl an möglichen Implantaten für den {JawTranslation[jaw]} erreicht.</WarningText>*/}
          {/*</Warning>}*/}
        </Right>
      </Top>
    </Container>)
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const Left = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
`
const Right = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0;
  justify-content: space-between;
  text-align: center;
  ${breakpoint('tablet')`
    flex: 0 0 350px;
    padding-left: 40px;
    text-align: left;
  `}
`

const ImageContainer = styled.div`
  flex: 0 0 300px;
  text-align: center;
  ${breakpoint('tablet')`
    text-align: left;
  `}
`

const Top = styled.div`
  display: flex;
  flex-direction: column-reverse;
  min-height: 400px;
  ${breakpoint('tablet')`
    flex-direction: row;
  `}
`

const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 25px;
`

const RadioButton = styled(RadioButtonControl)`
  margin-bottom: 10px;
`

const NoTeethImage = styled.img`
  width: 100%;
  max-width: 376px;
`

const MissingTeethList = styled.ul`
  margin: 15px 0;
  min-height: 45px;
  ${breakpoint('tablet')`
    margin: 30px 0 0 0;
  `}
`

const MissingTooth = styled.li`
  display: inline-block;
  width: 40px;
  height: 40px;
  background: ${props => props.theme.primaryColor};
  border-radius: 50%;
  margin-right: 5px;
  margin-bottom: 5px;
  line-height: 40px;
  text-align: center;
  font-size: 13px;
  color: white;
  font-weight: bold;
  ${breakpoint('tablet')`
    width: 50px;
    height: 50px;
    margin-right: 10px;
    margin-bottom: 10px;
    line-height: 50px;
    font-size: 18px;
  `}
`
